/**
 * useInvoiceData Hook
 * 
 * This hook manages invoice data transformations, form state,
 * and related data processing to reduce complexity in components.
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { InvoiceRecord, StoreRecord, SupplierRecord } from '@types';

const DEFAULT_CLASSIFICATION = 'deliver_direct' as const;
const UNPROCESSED_STATUS = 'unprocessed' as const;

interface UseInvoiceDataProps {
  invoice?: InvoiceRecord;
  storesData?: { data: any[] };
  suppliersData?: { data: SupplierRecord[] };
}

// Helper function to transform invoice data (outside component to avoid re-creation)
const createFormDataFromInvoice = (invoice: any): Partial<InvoiceRecord> => ({
  supplier_id: invoice.supplier_id,
  supplier: invoice.supplier,
  invoice_number: invoice.invoice_number,
  invoice_date: invoice.invoice_date,
  due_date: invoice.due_date || '',
  total_amount: invoice.total_amount,
  supplier_abn: invoice.supplier_abn || '',
  classification: invoice.classification || DEFAULT_CLASSIFICATION,
  is_new_stock: invoice.is_new_stock || false,
  is_product_added: invoice.is_product_added || false,
  store_id: invoice.store_id || '',
  po: invoice.po || '',
  so: invoice.so || '',
  ir: invoice.ir || '',
  inv: invoice.inv || '',
  claim: invoice.claim || '',
  cn: invoice.cn || '',
  credit_amount: invoice.credit_amount || '',
  notes: invoice.notes || '',
});

// Helper function to transform store record
const transformStoreToRecord = (store: any): StoreRecord => ({
  ...store,
  created_at: store.createdAt || store.created_at || '',
  updated_at: store.updatedAt || store.updated_at || ''
});

export const useInvoiceData = ({ invoice, storesData, suppliersData }: UseInvoiceDataProps) => {
  const [formData, setFormData] = useState<Partial<InvoiceRecord>>({
    classification: DEFAULT_CLASSIFICATION
  });
  const [currentStatus, setCurrentStatus] = useState<string>(UNPROCESSED_STATUS);
  const [selectedStore, setSelectedStore] = useState<StoreRecord | null>(null);
  const [selectedSupplier, setSelectedSupplier] = useState<SupplierRecord | null>(null);

  // Initialize form data when invoice is loaded
  useEffect(() => {
    if (invoice) {
      setFormData(createFormDataFromInvoice(invoice));
      setCurrentStatus(invoice.status || UNPROCESSED_STATUS);
    }
  }, [invoice?.id]);

  // Update selectedStore when invoice or stores data changes
  useEffect(() => {
    if (invoice?.store_id && storesData?.data) {
      const store = storesData.data.find(store => store.id === invoice.store_id);
      if (store) {
        setSelectedStore(transformStoreToRecord(store));
      } else {
        setSelectedStore(null);
      }
    } else if (invoice && !invoice.store_id) {
      setSelectedStore(null);
    }
  }, [invoice?.store_id, storesData?.data]);

  // Initialize selectedSupplier when invoice and suppliers data are available
  useEffect(() => {
    if (invoice && suppliersData?.data) {
      let newSelectedSupplier: SupplierRecord | null = null;

      if (invoice?.supplier_id) {
        // First try to find by supplier_id (most reliable)
        const supplier = suppliersData.data.find(supplier => supplier.id === invoice.supplier_id);
        newSelectedSupplier = supplier || null;
      } else if (invoice.supplier) {
        // Try to find supplier by name and ABN as fallback
        const supplier = suppliersData.data.find(supplier =>
          supplier.name === invoice.supplier &&
          (!invoice.supplier_abn || supplier.abn === invoice.supplier_abn)
        );

        if (supplier) {
          newSelectedSupplier = supplier;
        } else {
          // Try flexible matching - ABN match优先 (most reliable)
          if (invoice.supplier_abn) {
            const abnMatch = suppliersData.data.find(supplier =>
              supplier.abn === invoice.supplier_abn
            );
            if (abnMatch) {
              newSelectedSupplier = abnMatch;
            }
          }

          // If no ABN match, try name-only match (case-insensitive)
          if (!newSelectedSupplier) {
            const normalizedName = invoice.supplier.toLowerCase().trim();
            const nameMatch = suppliersData.data.find(supplier =>
              supplier.name.toLowerCase().trim() === normalizedName
            );
            if (nameMatch) {
              newSelectedSupplier = nameMatch;
            }
          }
        }
      }

      // Only update if the supplier has actually changed (check by ID, not full object)
      const hasChanged = selectedSupplier?.id !== newSelectedSupplier?.id;
      if (hasChanged) {
        setSelectedSupplier(newSelectedSupplier);
      }
    }
  }, [invoice, suppliersData?.data]);

  // Memoized utility functions to build invoice data with better performance
  const buildBaseInvoiceData = useMemo(() => ({
    supplier: formData.supplier,
    invoice_number: formData.invoice_number,
    invoice_date: formData.invoice_date,
    due_date: formData.due_date,
    total_amount: formData.total_amount,
    supplier_abn: formData.supplier_abn,
    classification: formData.classification,
    notes: formData.notes,
  }), [formData]);

  // Memoized workflow fields (exclude in unprocessed status to avoid permission issues)
  const buildWorkflowData = useMemo(() => {
    // Don't include workflow fields in unprocessed status
    // This prevents permission errors when transitioning from unprocessed to other statuses
    if (currentStatus === 'unprocessed') {
      return {};
    }
    
    return {
      ...(formData.store_id && { store_id: formData.store_id }),
      ...(formData.po && { po: formData.po }),
      ...(formData.so && { so: formData.so }),
      ...(formData.ir && { ir: formData.ir }),
      ...(formData.inv && { inv: formData.inv }),
      // Include boolean workflow fields for non-unprocessed statuses
      ...(formData.is_product_added !== undefined && { is_product_added: formData.is_product_added }),
      ...(formData.is_new_stock !== undefined && { is_new_stock: formData.is_new_stock }),
    };
  }, [formData.store_id, formData.po, formData.so, formData.ir, formData.inv, formData.is_product_added, formData.is_new_stock, currentStatus]);

  // Memoized credit note fields (only include for credit note classification or status)
  const buildCreditNoteData = useMemo(() => {
    // Only include credit note fields if this is a credit note classification or status
    const isCreditNoteWorkflow = formData.classification === 'credit_note' || 
                                currentStatus === 'credit_note' || 
                                currentStatus === 'credit_entering';
    
    if (!isCreditNoteWorkflow) {
      return {};
    }
    
    return {
      ...(formData.claim && { claim: formData.claim }),
      ...(formData.cn && { cn: formData.cn }),
      ...(formData.credit_amount && { credit_amount: formData.credit_amount }),
    };
  }, [formData.claim, formData.cn, formData.credit_amount, formData.classification, currentStatus]);

  // Memoized invoice payload builder
  const buildInvoicePayload = useMemo(() => ({
    ...buildBaseInvoiceData,
    ...buildWorkflowData,
    ...buildCreditNoteData,
  }), [buildBaseInvoiceData, buildWorkflowData, buildCreditNoteData]);

  // Input change handler
  const handleInputChange = useCallback((field: keyof InvoiceRecord, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  return {
    // State
    formData,
    setFormData,
    currentStatus,
    setCurrentStatus,
    selectedStore,
    setSelectedStore,
    selectedSupplier,
    setSelectedSupplier,

    // Computed values
    buildInvoicePayload,

    // Handlers
    handleInputChange,

    // Utilities
    transformStoreToRecord: useCallback(transformStoreToRecord, []),
  };
};
